import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SystemRole } from '@peles/shared';
import { isAnyModalOpen } from '../components/common/EntityModal';
import {
  GO_SEQUENCE_MS,
  NEW_EVENT_PATH,
  goTargetsFor,
  isOverlayOpen,
  isTypingTarget,
} from '../components/command/shortcuts';

export interface GlobalShortcutHandlers {
  openSearch: () => void;
  openHelp: () => void;
}

/**
 * The app-wide keys (see `components/command/shortcuts.ts`). ⌘K / Ctrl+K opens
 * search from anywhere, even while typing; every other key is ignored while a
 * field has focus, a dialog, menu or popover is open, or a modifier is held,
 * so a shortcut never steals a letter somebody meant to type or acts behind
 * something on screen.
 */
export function useGlobalShortcuts(
  role: SystemRole | undefined,
  { openSearch, openHelp }: GlobalShortcutHandlers,
  enabled = true,
): void {
  const navigate = useNavigate();
  const goPending = useRef<number | null>(null);
  const handlers = useRef({ openSearch, openHelp });
  handlers.current = { openSearch, openHelp };

  useEffect(() => {
    if (!enabled) return;
    const targets = goTargetsFor(role);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const key = e.key.toLowerCase();

      if (key === 'k' && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        if (isAnyModalOpen() || isOverlayOpen()) return;
        e.preventDefault();
        handlers.current.openSearch();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target) || isAnyModalOpen() || isOverlayOpen()) return;

      if (goPending.current !== null) {
        const started = goPending.current;
        goPending.current = null;
        const target = targets.find((t) => t.key === key);
        if (target && Date.now() - started <= GO_SEQUENCE_MS) {
          e.preventDefault();
          navigate(target.to);
          return;
        }
      }

      if (e.key === '?') {
        e.preventDefault();
        handlers.current.openHelp();
      } else if (e.key === '/') {
        e.preventDefault();
        handlers.current.openSearch();
      } else if (key === 'g' && !e.shiftKey) {
        goPending.current = Date.now();
      } else if (key === 'n' && !e.shiftKey) {
        e.preventDefault();
        navigate(NEW_EVENT_PATH);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, role, navigate]);
}
